import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PanelService } from 'src/app/modules/shared/services/api/panel.service';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { StateService } from 'src/app/modules/shared/services/storage/state.service';
import { WalletBalanceService } from 'src/app/modules/shared/services/resolver/wallet-balance.service';
import { GoogleTagManagerService } from 'src/app/modules/shared/services/google-tag-manager.service';
import { DataLayerService } from 'src/app/modules/shared/services/data-layer/data-layer.service';
import { GoogleEventTypes } from 'src/app/modules/shared/enum/datalayer-event-types';
import { DeviceDetectorService } from 'src/app/modules/shared/services/device-detector/device-detector.service';
import { FACEBOOK_EVENT_TYPES } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.model';
import { MetaPixelService } from 'src/app/modules/shared/services/meta-pixel/meta-pixel.service';

@Component({
  selector: 'app-payment-status',
  templateUrl: './payment-status.component.html',
  styleUrls: ['./payment-status.component.scss'],
})
export class PaymentStatusComponent implements OnInit, OnDestroy {
  isSuccessful: boolean = false;
  remainingTimer = 10;
  remainingSpinner = 100;
  subscription: Subscription[] = [];
  paymentDetail: any;
  detail: any;
  productUrlFriendlyName: string;
  isDesktop: boolean;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private panelService: PanelService,
    private walletBalance: WalletBalanceService,
    private stateService: StateService,
    private dataLayerService: DataLayerService,
    private metaPixelService: MetaPixelService,
    private googleTagManagerService: GoogleTagManagerService,
    private deviceDetectorService: DeviceDetectorService
  ) {
    this.isDesktop = this.deviceDetectorService.isDesktop;
  }

  ngOnInit(): void {
    this.setRouteParam();
  }

  private getUrlFriendlyName(name: string) {
    return name.replace(/[^a-zA-Z0-9]+/g, '-');
  }

  ngOnDestroy() {
    this.subscription.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  setRouteParam() {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (
        !params.order_id &&
        !params.payment_id &&
        !params.wallet_payment &&
        !params.redeem
      ) {
        this.router.navigate(['/']);
      }
      if (params.redeem) {
        this.getOrderDetailsRedeem(params.redeem);
      }
      if (params.wallet_payment) {
        this.getOrderDetails(params.wallet_payment);
      }
      if (params.order_id) {
        this.validatePayment(params.order_id);
      }
      if (params.payment_id) {
        this.validatePaymentTopUp(params.payment_id);
      }
    });
  }

  getOrderDetailsRedeem(orderId: any) {
    this.panelService.getOrderDetail(orderId).subscribe(
      (data) => {
        this.paymentDetail = data;
        this.productUrlFriendlyName = this.getUrlFriendlyName(
          this.paymentDetail.product_detail.name
        );
        if (data?.status !== -1) {
          this.isSuccessful = true;
          this.googleTagManagerService.clearPreviousEcommerce();
          this.dataLayerService.pushGoogleEvent(GoogleEventTypes.PURCHASE, {
            currency: data?.gateway_detail.currency_detail?.code,
            transactionId: data?.wallet_transaction_id,
            payablePrice: data?.payable_amount,
            id: data?.product_detail?.id,
            name: data?.product_detail?.name,
            discount: data?.discount,
            price: data?.item_price,
            quantity: data?.item_count,
          });
          this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.PURCHASE, {
            data: {
              content_ids: [`${data.product_detail.id}`],
              contents: [
                {
                  id: `${data.product_detail.id}`,
                  quantity: data.item_count,
                },
              ],
              content_type: 'product',
              num_items: 1,
              currency: data.gateway_detail.currency_detail.code,
              value: data.payable_amount,
            },
          });
        }
      },
      (error) => {
        this.isSuccessful = false;
      }
    );
  }

  getOrderDetails(orderId: any) {
    this.panelService.getOrderDetail(orderId).subscribe(
      (data) => {
        this.paymentDetail = data;
        this.productUrlFriendlyName = this.getUrlFriendlyName(
          this.paymentDetail.product_detail.name
        );
        if (data?.status == 4 || data?.status == 6 || data?.status == 8) {
          this.isSuccessful = true;
          this.googleTagManagerService.clearPreviousEcommerce();
          this.dataLayerService.pushGoogleEvent(GoogleEventTypes.PURCHASE, {
            currency: data?.gateway_detail.currency_detail?.code,
            transactionId: data?.wallet_transaction_id,
            payablePrice: data?.payable_amount,
            id: data?.product_detail?.id,
            name: data?.product_detail?.name,
            discount: data?.discount,
            price: this.paymentDetail?.item_price,
            orderAmount: data?.order_amount,
            quantity: data?.item_count,
          });
          this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.PURCHASE, {
            data: {
              content_ids: [`${data.product_detail.id}`],
              contents: [
                {
                  id: `${data.product_detail.id}`,
                  quantity: data.item_count,
                },
              ],
              content_type: 'product',
              num_items: 1,
              currency: data.gateway_detail.currency_detail.code,
              value: data.payable_amount,
            },
          });
        }
      },
      (error) => {
        this.isSuccessful = false;
      }
    );
  }

  validatePayment(orderId) {
    this.panelService
      .validatePayment(orderId)
      .pipe(
        finalize(() => {
        })
      )
      .subscribe(
        (PaymentValidationData) => {
          this.detail = PaymentValidationData.detail;
          this.panelService.getOrderDetail(orderId).subscribe((OrderData) => {
            this.paymentDetail = OrderData;
            this.productUrlFriendlyName = this.getUrlFriendlyName(
              this.paymentDetail.product_detail.name
            );
            if (
              this.paymentDetail?.status == 6 ||
              this.paymentDetail?.status == 7 ||
              this.paymentDetail?.status == 8 ||
              this.paymentDetail?.status == 4
            ) {
              this.googleTagManagerService.clearPreviousEcommerce();
              this.dataLayerService.pushGoogleEvent(GoogleEventTypes.PURCHASE, {
                currency:
                  this.paymentDetail.gateway_detail.currency_detail.code,
                transactionId: this.paymentDetail?.wallet_transaction_id,
                payablePrice: this.paymentDetail?.payable_amount,
                id: this.paymentDetail?.product_detail?.id,
                name: this.paymentDetail?.product_detail?.name,
                discount: this.paymentDetail?.discount,
                orderAmount: this.paymentDetail?.order_amount,
                price: this.paymentDetail?.item_price,
                quantity: this.paymentDetail?.item_count,
              });
              this.metaPixelService.pushEvent(FACEBOOK_EVENT_TYPES.PURCHASE, {
                data: {
                  content_ids: [`${this.paymentDetail.product_detail.id}`],
                  contents: [
                    {
                      id: `${this.paymentDetail.product_detail.id}`,
                      quantity: this.paymentDetail.item_count,
                    },
                  ],
                  content_type: 'product',
                  num_items: 1,
                  currency:
                    this.paymentDetail.gateway_detail.currency_detail.code,
                  value: this.paymentDetail.payable_amount,
                },
              });
            }
          });
          this.isSuccessful =
            PaymentValidationData?.code !== 4216 &&
            PaymentValidationData?.code !== 4223;
        },
        (error) => {
          this.isSuccessful = false;
        }
      );
  }

  validatePaymentTopUp(paymentId) {
    this.panelService
      .validatePaymentTopUp(paymentId)
      .pipe(
        finalize(() => {
          this.redirectToWallet();
        })
      )
      .subscribe(
        (data) => {
          this.detail = data.detail;
          this.isSuccessful = true;
        },
        (error) => {
          this.isSuccessful = false;
        }
      );
  }

  redirectToSummary() {
    const spinnerSource = interval(95);
    const remainingSubscription = spinnerSource.subscribe((val) => {
      this.remainingSpinner -= 1;
    });
    this.subscription.push(remainingSubscription);

    const source = interval(1000);
    const spinnerSubscription = source.subscribe((val) => {
      this.remainingTimer -= 1;
      if (this.remainingTimer < 1) {
        if (this.isSuccessful == true) {
          this.router.navigate(['/order-history']);
        } else {
          this.router.navigate(['/']);
        }
      }
    });
    this.subscription.push(spinnerSubscription);
  }

  redirectToWallet() {
    const spinnerSource = interval(95);
    const remainingSubscription = spinnerSource.subscribe((val) => {
      this.remainingSpinner -= 1;
    });
    this.subscription.push(remainingSubscription);

    const source = interval(1000);
    const spinnerSubscription = source.subscribe((val) => {
      this.remainingTimer -= 1;
      if (this.remainingTimer < 1) {
        if (this.isSuccessful == true) {
          this.router.navigateByUrl(
            this.isDesktop ? '/profile?tab=wallet' : '/wallet'
          );
          this.stateService.setIsWalletReloaded(true);
          this.walletBalance.updateInfo();
        } else {
          this.router.navigate(['/']);
        }
      }
    });
    this.subscription.push(spinnerSubscription);
  }
}
